import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentReader } from '../../word/api/DocumentReader';
import { TrackChangesExtractor } from '../../word/utils/TrackChangesExtractor';
import apiService from '../../services/api';
import {
  documentIdentityKey,
  type DocumentIdentity,
} from '../../utils/reviewDocumentIdentity';
import {
  clearReviewStateForIdentity,
  loadReviewCompleteState,
  saveReviewCompleteState,
  saveReviewPartyResults,
} from '../../utils/reviewStateStorage';
import {
  REVIEW_COMPLETE_STATE_STORAGE_KEY,
  REVIEW_RESULTS_STORAGE_KEY,
} from '../../utils/reviewStateStorageKeys';
import { DocumentReviewResults } from './DocumentReviewResults';
import { ChatService } from '../../features/chat/ChatService';
import { directionForLanguage } from '../../i18n/config';
import { detectTextDirection, resolveInputDirection } from '../../utils/textUtils';
import './ReviewView.css';

type Step = 'loading' | 'question1a' | 'question1b' | 'question2' | 'reviewing' | 'reviewResults';

// Review findings types
interface ReviewFinding {
  paragraphIndex: number;
  explanation: {
    ourSideInterest: string;
    theirSideInterest: string;
    whyCritical?: string;
    whyWarning?: string;
    whyInfo?: string;
  };
}

interface ReviewFindings {
  critical: ReviewFinding[];
  warning: ReviewFinding[];
  info: ReviewFinding[];
}

export interface ReviewViewProps {
  /**
   * Called after party identification is persisted to localStorage (our side has at least one entity).
   * Parent may switch tabs (e.g. return user to Ask/Agent/Editor after a gated review flow).
   */
  onReviewContextCommitted?: () => void;
  /** Open document identity; Review cache restores only when this matches a stored snapshot. */
  documentIdentity: DocumentIdentity | null;
  /** True while Review is loading or scanning the document (bottom-nav busy indicator). */
  onBusyChange?: (busy: boolean) => void;
  chatService?: ChatService;
}

export const ReviewView: React.FC<ReviewViewProps> = ({
  onReviewContextCommitted,
  documentIdentity,
  onBusyChange,
  chatService,
}) => {
  const { t, i18n } = useTranslation('taskpane');
  const chromeDir = directionForLanguage(i18n.language);
  const [step, setStep] = React.useState<Step>('loading');
  const [entities, setEntities] = React.useState<string[]>([]);
  const [uniqueAuthors, setUniqueAuthors] = React.useState<string[]>([]);
  const [hasRedlines, setHasRedlines] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [subProjectId, setSubProjectId] = React.useState<string | null>(null);

  // Question 1 state
  const [selectedOurSide, setSelectedOurSide] = React.useState<string[]>([]);
  const [selectedTheirSide, setSelectedTheirSide] = React.useState<string[]>([]);
  const [optionalDetails, setOptionalDetails] = React.useState<string>('');

  // Question 2 state
  const [sideAssignments, setSideAssignments] = React.useState<Record<string, 'our_side' | 'their_side'>>({});
  const [isIdentifyingSides, setIsIdentifyingSides] = React.useState<boolean>(false);

  // Document review state
  const [reviewFindings, setReviewFindings] = React.useState<ReviewFindings | null>(null);
  const [expandedCategories, setExpandedCategories] = React.useState<Set<string>>(new Set(['critical', 'warning', 'info']));
  const [documentParagraphs, setDocumentParagraphs] = React.useState<Array<{ index: number; text: string }>>([]);

  const isBusy = step === 'loading' || step === 'reviewing';
  React.useEffect(() => {
    onBusyChange?.(isBusy);
  }, [isBusy, onBusyChange]);
  React.useEffect(() => () => onBusyChange?.(false), [onBusyChange]);
  const documentIdentityRef = React.useRef(documentIdentity);
  documentIdentityRef.current = documentIdentity;
  const bootstrapIdRef = React.useRef(0);
  const identityToken = documentIdentity
    ? documentIdentityKey(documentIdentity) ?? `${documentIdentity.name}|pending`
    : null;

  // Restore findings only when they belong to this document snapshot.
  React.useEffect(() => {
    if (!documentIdentity) {
      return;
    }

    bootstrapIdRef.current += 1;

    setEntities([]);
    setUniqueAuthors([]);
    setHasRedlines(false);
    setError(null);
    setSelectedOurSide([]);
    setSelectedTheirSide([]);
    setOptionalDetails('');
    setSideAssignments({});
    setIsIdentifyingSides(false);
    setReviewFindings(null);
    setExpandedCategories(new Set(['critical', 'warning', 'info']));
    setDocumentParagraphs([]);
    setStep('loading');

    const savedState = loadReviewCompleteState(documentIdentity, REVIEW_COMPLETE_STATE_STORAGE_KEY);
    if (savedState?.reviewFindings && savedState.documentParagraphs) {
      setSelectedOurSide(savedState.ourSide || []);
      setSelectedTheirSide(savedState.theirSide || []);
      setOptionalDetails(savedState.optionalDetails || '');
      setHasRedlines(savedState.hasRedlines || false);
      setSideAssignments(savedState.sideAssignments || {});
      setReviewFindings(savedState.reviewFindings as ReviewFindings);
      setDocumentParagraphs(savedState.documentParagraphs);
      setStep('reviewResults');
      console.log('Restored saved review state for', identityToken);
    } else {
      loadDocumentData();
    }
    checkForSubProject();
    // identityToken captures name + lastSaveTime (or createdAt); documentIdentity object may be new each time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityToken]);

  const checkForSubProject = () => {
    // Check if there's a sub-project ID in localStorage (set by other components)
    try {
      const stored = localStorage.getItem('selectedSubProjectId');
      if (stored) {
        setSubProjectId(stored);
      }
    } catch (err) {
      console.error('Error checking for sub-project:', err);
    }
  };

  const loadDocumentData = async () => {
    const bootstrapId = bootstrapIdRef.current;
    const stillCurrent = () => bootstrapId === bootstrapIdRef.current;
    try {
      setError(null);

      // Load independent document inputs in parallel to reduce first-load latency
      const [firstPageText, ooxml] = await Promise.all([
        DocumentReader.getFirstPageText(),
        DocumentReader.getFullDocumentOOXML(),
      ]);
      if (!stillCurrent()) return;

      // Extract entities from first page text via engine
      if (firstPageText && firstPageText.length > 0) {
        try {
          const extractedEntities = await apiService.extractEntities(firstPageText);
          if (!stillCurrent()) return;
          setEntities(extractedEntities);
        } catch (err) {
          console.error('Error extracting entities:', err);
          // Continue without entities
        }
      }

      // Check for track changes to determine if Question 2 is needed
      if (ooxml && ooxml.length > 0) {
        const changes = TrackChangesExtractor.extractTrackChanges(ooxml);
        if (changes.length > 0) {
          if (!stillCurrent()) return;
          setHasRedlines(true);
        }
      }

      if (!stillCurrent()) return;
      // Move to Question 1a (Who are you?)
      setStep('question1a');
    } catch (err: any) {
      console.error('Error loading document data:', err);
      if (!stillCurrent()) return;
      setError(err.message || t('review.errors.loadDocumentFailed'));
      setStep('question1a'); // Still allow user to proceed
    }
  };

  const toggleOurSide = (entity: string) => {
    setSelectedOurSide(prev => {
      if (prev.includes(entity)) {
        return prev.filter(e => e !== entity);
      } else {
        return [...prev, entity];
      }
    });
  };

  const toggleTheirSide = (entity: string) => {
    setSelectedTheirSide(prev => {
      if (prev.includes(entity)) {
        return prev.filter(e => e !== entity);
      } else {
        return [...prev, entity];
      }
    });
  };

  const handleQuestion1aSubmit = () => {
    if (selectedOurSide.length === 0) {
      setError(t('review.errors.selectOurSide'));
      return;
    }

    setError(null);
    // Move to Question 1b (Who is the other side?)
    setStep('question1b');
  };

  const handleQuestion1bSubmit = async () => {
    if (selectedTheirSide.length === 0) {
      setError(t('review.errors.selectTheirSide'));
      return;
    }

    setError(null);

    // If there are redlines, move to Question 2
    if (hasRedlines) {
      setStep('question2');
      // Load authors from identify sides API if subProjectId is available
      if (subProjectId) {
        await loadAuthorsFromAPI();
      } else {
        // Fallback: extract authors from track changes locally
        await loadAuthorsFromTrackChanges();
      }
    } else {
      // No redlines, start document review directly
      saveResults();
      notifyReviewContextCommittedIfReady();
      await startDocumentReview();
    }
  };

  const loadAuthorsFromAPI = async () => {
    if (!subProjectId) return;

    try {
      setIsIdentifyingSides(true);
      const authors = await apiService.identifySides(subProjectId);
      setUniqueAuthors(authors);
      
      // Try to load existing side assignments
      try {
        const existingAssignments = await apiService.getSideAssignments(subProjectId);
        if (Object.keys(existingAssignments).length > 0) {
          setSideAssignments(existingAssignments);
        }
      } catch (err) {
        console.error('Error loading existing side assignments:', err);
      }
    } catch (err: any) {
      console.error('Error identifying sides from API:', err);
      // Fallback to local extraction
      await loadAuthorsFromTrackChanges();
    } finally {
      setIsIdentifyingSides(false);
    }
  };

  const loadAuthorsFromTrackChanges = async () => {
    try {
      const ooxml = await DocumentReader.getFullDocumentOOXML();
      if (ooxml && ooxml.length > 0) {
        const changes = TrackChangesExtractor.extractTrackChanges(ooxml);
        const authors = Array.from(new Set(changes.map(c => c.author)));
        setUniqueAuthors(authors);
      }
    } catch (err) {
      console.error('Error extracting authors from track changes:', err);
    }
  };

  const handleQuestion2Submit = async () => {
    // Validate all authors are assigned
    const allAssigned = uniqueAuthors.every(author => sideAssignments[author]);
    
    if (!allAssigned) {
      setError(t('review.errors.assignAllAuthors'));
      return;
    }

    setError(null);

    // Save side assignments if subProjectId is available
    if (subProjectId) {
      try {
        await apiService.saveSideAssignments(subProjectId, sideAssignments);
      } catch (err) {
        console.error('Error saving side assignments:', err);
        // Continue anyway
      }
    }

    saveResults();
    notifyReviewContextCommittedIfReady();
    // Start document review instead of showing complete
    await startDocumentReview();
  };

  const startDocumentReview = async () => {
    const identityAtStart = documentIdentityRef.current;
    const bootstrapId = bootstrapIdRef.current;
    const stillCurrent = () => bootstrapId === bootstrapIdRef.current;
    try {
      const startedAt = Date.now();
      setStep('reviewing');
      setError(null);

      // Findings are computed on accept-all wording (insertions kept, deletions
      // dropped). The results UI still paints live redlines from paragraph OOXML.
      const contextStart = Date.now();
      const { fullText, paragraphs } = await DocumentReader.getFullDocumentContext();
      const contextMs = Date.now() - contextStart;
      if (!stillCurrent()) return;
      
      // Store paragraphs for later use in rendering findings
      setDocumentParagraphs(paragraphs);

      // Prepare review context
      const reviewContext = {
        ourSide: selectedOurSide,
        theirSide: selectedTheirSide,
        sideAssignments: hasRedlines ? sideAssignments : undefined,
        optionalDetails: optionalDetails || undefined,
      };

      // Call API to review document
      const apiStart = Date.now();
      const findings = await apiService.reviewDocument(fullText, paragraphs, reviewContext);
      const apiMs = Date.now() - apiStart;
      if (!stillCurrent()) return;
      console.log('[Perf][ReviewFlow] review_document_done', {
        contextMs,
        apiMs,
        totalMs: Date.now() - startedAt,
        fullTextLength: fullText.length,
        paragraphsCount: paragraphs.length,
        ourSideCount: reviewContext.ourSide.length,
        theirSideCount: reviewContext.theirSide.length,
        sideAssignmentsCount: reviewContext.sideAssignments ? Object.keys(reviewContext.sideAssignments).length : 0,
        critical: findings?.critical?.length ?? 0,
        warning: findings?.warning?.length ?? 0,
        info: findings?.info?.length ?? 0,
      });
      
      setReviewFindings(findings);
      setDocumentParagraphs(paragraphs);
      setStep('reviewResults');

      if (identityAtStart) {
        saveReviewCompleteState(identityAtStart, REVIEW_COMPLETE_STATE_STORAGE_KEY, {
          ourSide: selectedOurSide,
          theirSide: selectedTheirSide,
          optionalDetails,
          hasRedlines,
          sideAssignments: hasRedlines ? sideAssignments : {},
          reviewFindings: findings,
          documentParagraphs: paragraphs,
        });
        console.log('Complete review state saved for', documentIdentityKey(identityAtStart));
      }
    } catch (err: any) {
      console.error('Error reviewing document:', err);
      if (!stillCurrent()) return;
      setError(err.message || t('review.errors.reviewFailed'));
      setStep('reviewResults'); // Still show results even if empty
    }
  };


  const saveResults = () => {
    const identity = documentIdentityRef.current;
    if (!identity) return;

    saveReviewPartyResults(identity, REVIEW_RESULTS_STORAGE_KEY, {
      ourSide: selectedOurSide,
      theirSide: selectedTheirSide,
      optionalDetails,
      hasRedlines,
      sideAssignments: hasRedlines ? sideAssignments : {},
    });
    console.log('Review results saved for', documentIdentityKey(identity));
  };

  /** Same as legacy `onSidesIdentified`: full submit paths only — not Skip. */
  const notifyReviewContextCommittedIfReady = () => {
    if (selectedOurSide.filter(Boolean).length > 0) {
      onReviewContextCommitted?.();
    }
  };

  const addManualEntity = (side: 'our' | 'their', value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    
    const entities = trimmed.split(',').map(e => e.trim()).filter(e => e.length > 0);
    
    if (side === 'our') {
      setSelectedOurSide(prev => {
        const newEntities = entities.filter(e => !prev.includes(e));
        return [...prev, ...newEntities];
      });
    } else {
      setSelectedTheirSide(prev => {
        const newEntities = entities.filter(e => !prev.includes(e));
        return [...prev, ...newEntities];
      });
    }
  };

  const handleSkip = async () => {
    if (step === 'question1a') {
      // Skip "Who are you?" - move to "Who is the other side?"
      setStep('question1b');
    } else if (step === 'question1b') {
      // Skip "Who is the other side?" - move to Question 2 if redlines exist, otherwise start review
      if (hasRedlines) {
        setStep('question2');
        if (subProjectId) {
          loadAuthorsFromAPI();
        } else {
          loadAuthorsFromTrackChanges();
        }
      } else {
        saveResults();
        await startDocumentReview();
      }
    } else if (step === 'question2') {
      // Skip Question 2, start document review
      saveResults();
      await startDocumentReview();
    }
  };

  const handleBack = () => {
    setError(null);
    if (step === 'question1b') {
      setStep('question1a');
    } else if (step === 'question2') {
      setStep('question1b');
    }
  };

  const renderQuestionsFooter = (
    onContinue: () => void,
    continueDisabled: boolean,
    showBack: boolean,
  ) => (
    <div className="questions-footer">
      <div className="questions-footer-start">
        {showBack ? (
          <button type="button" onClick={handleBack} className="button-back">
            <span className="button-arrow" aria-hidden="true">
              ←
            </span>
            {t('review.back')}
          </button>
        ) : null}
      </div>
      <div className="questions-footer-end">
        <button type="button" onClick={handleSkip} className="button-skip">
          {t('review.skip')}
        </button>
        <button
          type="button"
          onClick={onContinue}
          className="button-continue"
          disabled={continueDisabled}
        >
          {t('review.continue')}
          <span className="button-arrow" aria-hidden="true">
            →
          </span>
        </button>
      </div>
    </div>
  );

  const handleRestart = () => {
    // Clear all state
    setStep('loading');
    setEntities([]);
    setUniqueAuthors([]);
    setHasRedlines(false);
    setError(null);
    setSelectedOurSide([]);
    setSelectedTheirSide([]);
    setOptionalDetails('');
    setSideAssignments({});
    setIsIdentifyingSides(false);
    setReviewFindings(null);
    setExpandedCategories(new Set(['critical', 'warning', 'info']));
    setDocumentParagraphs([]);
    
    const identity = documentIdentityRef.current;
    if (identity) {
      clearReviewStateForIdentity(
        identity,
        REVIEW_COMPLETE_STATE_STORAGE_KEY,
        REVIEW_RESULTS_STORAGE_KEY,
      );
    }
    
    bootstrapIdRef.current += 1;
    // Reload document data to start fresh
    void loadDocumentData();
  };

  // Loading state
  if (step === 'loading') {
    return (
      <div className="review-view" dir={chromeDir}>
        <div className="review-loading">
          <div className="spinner"></div>
          <span>{t('review.loadingDocument')}</span>
        </div>
      </div>
    );
  }

  // Question 1a: Who are you?
  if (step === 'question1a') {
    // Filter out entities that are already selected for the other side
    const availableEntities = entities.filter(e => !selectedTheirSide.includes(e));
    
    return (
      <div className="review-view" dir={chromeDir}>
        <div className="questions-header">
          <h2 className="questions-title">{t('review.questions')}</h2>
          <span className="questions-progress">
            {t('review.progress', { current: 1, total: hasRedlines ? 3 : 2 })}
          </span>
        </div>

        <div className="question-container">
          <h3 className="question-text">{t('review.whoAreYou')}</h3>
          
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="options-list">
            {availableEntities.length > 0 ? (
              <>
                {availableEntities.map((entity, idx) => {
                  const letter = String.fromCharCode(65 + (idx % 26)); // A, B, C, D, etc.
                  const isSelected = selectedOurSide.includes(entity);
                  
                  return (
                    <div
                      key={idx}
                      className={`option-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleOurSide(entity)}
                    >
                      <div className="option-letter">{letter}</div>
                      <div className="option-text" dir={detectTextDirection(entity)}>{entity}</div>
                      {isSelected && <div className="option-checkmark">✓</div>}
                    </div>
                  );
                })}
              </>
            ) : (
              <div className="options-placeholder">
                <p>{t('review.noEntities')}</p>
              </div>
            )}
          </div>

          <div className="manual-input-section">
            <div className="form-group">
              <label htmlFor="our-side">{t('review.yourSide')}</label>
              <input
                id="our-side"
                type="text"
                dir="auto"
                placeholder={t('review.enterEntities')}
                className="review-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    addManualEntity('our', (e.target as HTMLInputElement).value);
                    (e.target as HTMLInputElement).value = '';
                  }
                }}
              />
            </div>
          </div>

          {selectedOurSide.length > 0 && (
            <div className="selected-entity-display">
              <p>{t('review.selected', { count: selectedOurSide.length })}</p>
              <div className="selected-entities-list">
                {selectedOurSide.map((entity, idx) => (
                  <span key={idx} className="selected-entity-tag" dir={detectTextDirection(entity)}>
                    {entity}
                    <button
                      className="remove-entity-btn"
                      onClick={() => toggleOurSide(entity)}
                      aria-label={t('review.removeEntity', { entity })}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {renderQuestionsFooter(handleQuestion1aSubmit, selectedOurSide.length === 0, false)}
      </div>
    );
  }

  // Question 1b: Who is the other side?
  if (step === 'question1b') {
    // Filter out entities that were selected as "our side"
    const availableEntities = entities.filter(e => !selectedOurSide.includes(e));
    
    return (
      <div className="review-view" dir={chromeDir}>
        <div className="questions-header">
          <h2 className="questions-title">{t('review.questions')}</h2>
          <span className="questions-progress">
            {t('review.progress', { current: 2, total: hasRedlines ? 3 : 2 })}
          </span>
        </div>

        <div className="question-container">
          <h3 className="question-text">{t('review.whoIsOtherSide')}</h3>
          
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="options-list">
            {availableEntities.length > 0 ? (
              <>
                {availableEntities.map((entity, idx) => {
                  const letter = String.fromCharCode(65 + (idx % 26)); // A, B, C, D, etc.
                  const isSelected = selectedTheirSide.includes(entity);
                  
                  return (
                    <div
                      key={idx}
                      className={`option-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleTheirSide(entity)}
                    >
                      <div className="option-letter">{letter}</div>
                      <div className="option-text" dir={detectTextDirection(entity)}>{entity}</div>
                      {isSelected && <div className="option-checkmark">✓</div>}
                    </div>
                  );
                })}
              </>
            ) : (
              <div className="options-placeholder">
                <p>{t('review.noOtherEntities')}</p>
              </div>
            )}
          </div>

          <div className="manual-input-section">
            <div className="form-group">
              <label htmlFor="their-side">{t('review.otherSide')}</label>
              <input
                id="their-side"
                type="text"
                dir="auto"
                placeholder={t('review.enterEntities')}
                className="review-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    addManualEntity('their', (e.target as HTMLInputElement).value);
                    (e.target as HTMLInputElement).value = '';
                  }
                }}
              />
            </div>
          </div>

          {selectedTheirSide.length > 0 && (
            <div className="selected-entity-display">
              <p>{t('review.selected', { count: selectedTheirSide.length })}</p>
              <div className="selected-entities-list">
                {selectedTheirSide.map((entity, idx) => (
                  <span key={idx} className="selected-entity-tag" dir={detectTextDirection(entity)}>
                    {entity}
                    <button
                      className="remove-entity-btn"
                      onClick={() => toggleTheirSide(entity)}
                      aria-label={t('review.removeEntity', { entity })}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="optional-details">
            <input
              type="text"
              value={optionalDetails}
              onChange={(e) => setOptionalDetails(e.target.value)}
              placeholder={t('review.addOptionalDetails')}
              className="optional-input"
              dir={resolveInputDirection(optionalDetails, t('review.addOptionalDetails'))}
            />
          </div>
        </div>

        {renderQuestionsFooter(handleQuestion1bSubmit, selectedTheirSide.length === 0, true)}
      </div>
    );
  }

  // Question 2: Identify sides from track change authors
  if (step === 'question2') {
    return (
      <div className="review-view" dir={chromeDir}>
        <div className="questions-header">
          <h2 className="questions-title">{t('review.questions')}</h2>
          <span className="questions-progress">{t('review.progress', { current: 3, total: 3 })}</span>
        </div>

        <div className="question-container">
          <h3 className="question-text">
            {t('review.authorsAssign', { count: uniqueAuthors.length })}
          </h3>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {isIdentifyingSides ? (
            <div className="review-loading">
              <div className="spinner"></div>
              <span>{t('review.identifyingAuthors')}</span>
            </div>
          ) : (
            <div className="options-list">
              {uniqueAuthors.map((author, idx) => {
                const letter = String.fromCharCode(65 + idx);
                const assignment = sideAssignments[author];
                
                return (
                  <div key={author} className="option-item">
                    <div className="option-letter">{letter}</div>
                    <div className="option-text" dir={detectTextDirection(author)}>{author}</div>
                    <div className="assignment-buttons">
                      <button
                        onClick={() => setSideAssignments(prev => ({ ...prev, [author]: 'our_side' }))}
                        className={`assignment-button ${assignment === 'our_side' ? 'active our-side' : ''}`}
                      >
                        {t('review.ourSide')}
                      </button>
                      <button
                        onClick={() => setSideAssignments(prev => ({ ...prev, [author]: 'their_side' }))}
                        className={`assignment-button ${assignment === 'their_side' ? 'active their-side' : ''}`}
                      >
                        {t('review.theirSide')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {renderQuestionsFooter(
          handleQuestion2Submit,
          !uniqueAuthors.every((author) => sideAssignments[author]) || isIdentifyingSides,
          true,
        )}
      </div>
    );
  }

  // Reviewing state
  if (step === 'reviewing') {
    return (
      <div className="review-view" dir={chromeDir}>
        <div className="review-loading">
          <div className="spinner"></div>
          <span>{t('review.reviewingDocument')}</span>
        </div>
      </div>
    );
  }

  // Review results state
  if (step === 'reviewResults') {
    return (
      <div className="review-view" dir={chromeDir}>
        {error && (
          <div className="error-message" style={{ margin: '16px' }}>
            {error}
            <button 
              onClick={startDocumentReview}
              style={{ marginInlineStart: '12px', padding: '4px 8px', fontSize: '12px' }}
            >
              {t('actions.retry', { ns: 'common' })}
            </button>
          </div>
        )}
        <DocumentReviewResults 
          findings={reviewFindings}
          expandedCategories={expandedCategories}
          onToggleCategory={(category) => {
            setExpandedCategories(prev => {
              const newSet = new Set(prev);
              if (newSet.has(category)) {
                newSet.delete(category);
              } else {
                newSet.add(category);
              }
              return newSet;
            });
          }}
          paragraphs={documentParagraphs}
          onRestart={handleRestart}
          chatService={chatService}
        />
      </div>
    );
  }

  return null;
};
